import { useMemo } from 'react'
import { shallowEqual, useAppSelector } from 'hooks/reduxHooks'
import generateOccupancyStringByRoom from 'lib/offer/generateOccupancyStringByRoom'
import { UseBundleOfferDataParams, BundleOfferData, HotelDataByDate } from './types'

/**
 * Custom hook to handle single offer calendar data
 *
 * This hook is a pure data consumer that reads calendar data from Redux.
 * It does NOT dispatch any fetch actions - it relies on the parent component
 * (e.g., FlashHotelContent.fetchCalendarData) to fetch the data.
 *
 * This hook handles:
 * - Reading calendar data for a specific offer from Redux
 * - Extracting hotel prices for date ranges from calendar
 * - Optimized for single offer (not offer lists)
 *
 * @param params - Configuration parameters including dateRangesByMonth
 * @returns BundleOfferData object containing hotel data for the date ranges
 */
export function useBundleOfferData(params: UseBundleOfferDataParams): BundleOfferData {
  const {
    offer,
    rooms,
    dateRangesByMonth,
    multipleRoomBookingActive = false,
    nights,
  } = params

  // Get the package for the current duration
  const currentPackage = useMemo(() => {
    return offer.packages.find(pkg => pkg.duration === nights)
  }, [offer.packages, nights])

  // Get calendar data from Redux (data should be fetched by parent component)
  const calendarData = useAppSelector(state => {
    if (!currentPackage) return null

    if (multipleRoomBookingActive) {
      // For multiple room, we need to aggregate data from all occupancy calendars
      // This will be handled in the hotelDataByDate calculation below
      return null
    } else {
      // Single room: get from calendarsByPackageKey
      return state.calendar.calendarsByPackageKey[currentPackage.uniqueKey]
    }
  })

  // Get multiple room calendar data from Redux (data should be fetched by parent component)
  const multiRoomCalendarData = useAppSelector(state => {
    if (!multipleRoomBookingActive || !currentPackage) return null

    const occupancyCalendars: Record<string, App.Calendar> = {}
    let isAnyFetching = false

    rooms.forEach(room => {
      const occupancyKey = generateOccupancyStringByRoom(room)
      const occupancyCalendar = state.calendar.calendarsByOccupancy[occupancyKey]?.[currentPackage.uniqueKey]
      const isFetching = state.calendar.calendarsByOccupancyLoading[occupancyKey]?.[currentPackage.uniqueKey] ?? false

      if (occupancyCalendar) {
        occupancyCalendars[occupancyKey] = occupancyCalendar
      }
      if (isFetching) {
        isAnyFetching = true
      }
    })

    return {
      calendars: occupancyCalendars,
      isFetching: isAnyFetching,
    }
  }, shallowEqual)

  // Extract hotel data for charter weekdays only
  const hotelDataByDate = useMemo(() => {
    const dataMap: Record<string, HotelDataByDate> = {}
    const allCheckInDates = Object.values(dateRangesByMonth).flat().map(range => range.checkIn)

    // Check if currentPackage exists - if not, mark all dates as having no package
    const hasNoPackage = !currentPackage

    if (multipleRoomBookingActive) {
      // Multiple room: aggregate data from all occupancy calendars
      if (!multiRoomCalendarData) {
        // Still loading or no package, mark all weekdays accordingly
        allCheckInDates.forEach(checkInDate => {
          dataMap[checkInDate] = {
            fetching: !hasNoPackage, // Only fetching if package exists
            hasNoPackage,
          }
        })
        return dataMap
      }

      // Aggregate data across all rooms for each weekday
      allCheckInDates.forEach(checkInDate => {
        const roomPrices: Array<number> = []
        const roomMemberPrices: Array<number> = []
        const roomValues: Array<number> = []
        const roomsLeftValues: Array<number> = []
        let totalOfferCount = 0
        let isAnyRoomFetching = false

        rooms.forEach(room => {
          const occupancyKey = generateOccupancyStringByRoom(room)
          const occupancyCalendar = multiRoomCalendarData.calendars[occupancyKey]

          if (!occupancyCalendar) {
            isAnyRoomFetching = true
            return
          }

          // Find the day in this occupancy calendar
          occupancyCalendar.months.forEach(month => {
            month.days.forEach(day => {
              if (day.checkIn === checkInDate) {
                if (day.hotelAvailable) {
                  roomPrices.push(day.hotelTotal)
                  if (day.hotelMemberTotal) {
                    roomMemberPrices.push(day.hotelMemberTotal)
                  }
                  // Extract hotelValue for "value up to" display
                  if (day.hotelValue !== undefined) {
                    roomValues.push(day.hotelValue)
                  }
                  // Extract roomsLeft from calendar day
                  if (day.availableRooms !== undefined) {
                    roomsLeftValues.push(day.availableRooms)
                  }
                  totalOfferCount += 1
                }
              }
            })
          })
        })

        // For multiple rooms, we need to SUM all room prices, not take the minimum
        // Each occupancy calendar returns the price for that specific room configuration
        const totalPrice = roomPrices.length > 0 ? roomPrices.reduce((sum, price) => sum + price, 0) : undefined
        const totalMemberPrice = roomMemberPrices.length > 0 ? roomMemberPrices.reduce((sum, price) => sum + price, 0) : undefined
        const totalValue = roomValues.length > 0 ? roomValues.reduce((sum, value) => sum + value, 0) : undefined
        // Get minimum roomsLeft across all rooms (similar to seatsLeft logic)
        const roomsLeft = roomsLeftValues.length > 0 ? Math.min(...roomsLeftValues) : undefined

        dataMap[checkInDate] = {
          fetching: multiRoomCalendarData.isFetching || isAnyRoomFetching,
          lowestPrice: totalPrice,
          lowestMemberPrice: totalMemberPrice,
          lowestValue: totalValue,
          offerCount: totalOfferCount,
          roomsLeft,
          hasNoPackage,
        }
      })
    } else {
      // Single room: use regular calendar data
      if (!calendarData) {
        // Calendar is still loading or no package, mark all weekdays accordingly
        allCheckInDates.forEach(checkInDate => {
          dataMap[checkInDate] = {
            fetching: !hasNoPackage, // Only fetching if package exists
            hasNoPackage,
          }
        })
        return dataMap
      }

      // Extract data from calendar months
      calendarData.months.forEach(month => {
        month.days.forEach(day => {
          // Only include dates that match our charter weekdays
          if (allCheckInDates.includes(day.checkIn)) {
            dataMap[day.checkIn] = {
              fetching: false,
              lowestPrice: day.hotelAvailable ? day.hotelTotal : undefined,
              lowestMemberPrice: day.hotelMemberTotal || undefined,
              lowestValue: day.hotelAvailable ? day.hotelValue : undefined,
              offerCount: day.hotelAvailable ? 1 : 0,
              roomsLeft: day.hotelAvailable ? day.availableRooms : undefined,
              hasNoPackage,
            }
          }
        })
      })
    }

    // Fill in any missing weekdays (not in calendar response)
    allCheckInDates.forEach(checkInDate => {
      if (!dataMap[checkInDate]) {
        dataMap[checkInDate] = {
          fetching: false,
          lowestPrice: undefined,
          lowestMemberPrice: undefined,
          offerCount: 0,
          hasNoPackage,
        }
      }
    })

    return dataMap
  }, [calendarData, multiRoomCalendarData, dateRangesByMonth, multipleRoomBookingActive, rooms, currentPackage])

  return {
    hotelDataByDate
  }
}
